// ─── Inventory: stock, stockout, money in transit, summary ────────────────────
import { parseDate, getDaysInMonth, mkKey } from "./utils.js";
import { getOrdersForPool } from "./marketplaces.js";

// Shrinkage multiplier — single source of truth for "N% of arrivals are
// lost to damage/misplacement/theft". Every function that subtracts
// shrinkage from arriving units MUST use this helper so the semantics are
// consistent and cannot drift. Tests verify that shrinkage is applied
// exactly once per inventory pipeline (no compounding when
// computeCurrentStock output is fed into computeStockoutDate: the former
// shrinks past arrivals, the latter shrinks future arrivals — disjoint sets).
export function shrinkageMultiplier(shrinkagePct) {
  const pct = Number(shrinkagePct) || 0;
  if (pct <= 0) return 1;
  return 1 - pct / 100;
}

// ─── Stock resolution hierarchy (Phase B of stock work) ─────────────────────
// Today's date helper for "anchor as of now". Tests can pass an explicit
// `today` to keep behavior deterministic.
function todayString(d = new Date()) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
}

// Stage A.1 — pick the effective stock for ONE marketplace.
// Returns { units, date, source } or null if nothing resolved.
//
// Cascade (highest priority first):
//   a. mkt.stockOverridden + mkt.currentStockOverride  → "manual_mkt"
//   b. Σ products' currentStock (when ANY product has it set)  → "manual_product"
//   c. mkt.lastSyncStock (with lastSyncStockDate)              → "api_mkt"
//   d. Σ products' apiStock                                    → "api_product"
//   e. null
export function effectiveMarketplaceStock(mkt, today = new Date()) {
  if (!mkt) return null;
  // a. Manual marketplace override
  // Allow override to 0 — the boolean flag is the intent, not the value
  if (mkt.stockOverridden) {
    return {
      units: Number(mkt.currentStockOverride) || 0,
      date: todayString(today),
      source: "manual_mkt",
    };
  }
  // b. Sum of per-product manual overrides
  let manualSum = 0;
  let manualCount = 0;
  for (const p of mkt.products || []) {
    if (p?._decoupled) continue;
    const v = Number(p.currentStock) || 0;
    if (v > 0) { manualSum += v; manualCount++; }
  }
  if (manualSum > 0) {
    return { units: manualSum, date: todayString(today), source: "manual_product" };
  }
  // c. Marketplace API total. Subtract the apiStock of any decoupled
  // products so decoupling a SKU immediately removes its units from every
  // downstream KPI — without waiting for a re-sync. The stored
  // lastSyncStock is frozen at sync time and doesn't know about the
  // _decoupled flag, so we do the correction on read.
  if (Number(mkt.lastSyncStock) > 0) {
    let decoupledUnits = 0;
    for (const p of mkt.products || []) {
      if (!p?._decoupled) continue;
      decoupledUnits += Number(p.apiStock) || Number(p.currentStock) || 0;
    }
    const adjusted = Math.max(0, Number(mkt.lastSyncStock) - decoupledUnits);
    return {
      units: adjusted,
      date: mkt.lastSyncStockDate || todayString(today),
      source: "api_mkt",
    };
  }
  // d. Sum of per-product API stock
  let apiSum = 0;
  let oldestApiDate = null;
  for (const p of mkt.products || []) {
    if (p?._decoupled) continue;
    const v = Number(p.apiStock) || 0;
    if (v > 0) {
      apiSum += v;
      const d = p.apiStockDate;
      if (d && (!oldestApiDate || d < oldestApiDate)) oldestApiDate = d;
    }
  }
  if (apiSum > 0) {
    return { units: apiSum, date: oldestApiDate || todayString(today), source: "api_product" };
  }
  return null;
}

// Stage A.2 — pick the baseline anchor for an inventory pool.
// Returns { units, date, source } following the cascade:
//   1. pool.stockOverridden + pool.initialStock  → "manual_pool"
//   2. Σ effectiveMarketplaceStock(mkt) for marketplaces in this pool
//      → label inherits from the dominant contributor (largest slice)
//   3. pool.initialStock (without override flag)  → "pool_baseline"
//   4. legacy.initialStock                         → "legacy_global"
//   5. zero                                        → "none"
//
// `legacy` is the legacy global state passed by computeInventorySummary
// (the brand-settings initialStock for users who never created a pool).
//
// Special case: when the pool is marked as SHARED (pool.isShared === true),
// the pool's own initialStock is the absolute source of truth — always.
// Per-marketplace stock fields become advisory only because the user has
// explicitly told us "this is one physical bucket". The shared-pool stock
// is set by the merge dialog when the user marks the pool as shared.
//
// Date fallback: when an anchor source has no date attached, fall back to
// the earliest `lastSyncStockDate` of the pool's marketplaces — that's the
// closest thing to "when did we first see this stock". This stops the
// projection chart from extrapolating from "today" when the anchor is older.
function earliestApiSyncDate(marketplaces, mktIds) {
  if (!Array.isArray(marketplaces) || !mktIds?.length) return null;
  const set = new Set(mktIds);
  let earliest = null;
  for (const m of marketplaces) {
    if (!set.has(m.id)) continue;
    const d = m.lastSyncStockDate;
    if (d && (!earliest || d < earliest)) earliest = d;
  }
  return earliest;
}

export function effectivePoolStockAnchor(pool, marketplaces, legacy, today = new Date()) {
  // Helper: pick the best date — provided value > earliest API sync > today
  const fallbackDate = (provided) => {
    if (provided) return provided;
    const apiDate = earliestApiSyncDate(marketplaces, pool?.marketplaceIds);
    return apiDate || todayString(today);
  };

  // 0. Shared pool — if stock is explicitly set (overridden or > 0), use it.
  // If stock is 0 and NOT overridden, fall through to per-marketplace cascade
  // so API-synced stock isn't silently ignored.
  if (pool && pool.isShared && (pool.stockOverridden || Number(pool.initialStock) > 0)) {
    return {
      units: Number(pool.initialStock) || 0,
      date: fallbackDate(pool.initialStockDate),
      source: "shared_pool",
    };
  }
  // 1. Manual pool override — allow override to 0 (boolean flag is the intent)
  if (pool && pool.stockOverridden) {
    return {
      units: Number(pool.initialStock) || 0,
      date: fallbackDate(pool.initialStockDate),
      source: "manual_pool",
    };
  }
  // 2. Per-marketplace cascade summed
  if (pool && Array.isArray(marketplaces) && marketplaces.length > 0) {
    const poolMktIds = new Set(pool.marketplaceIds || []);
    const contributors = [];
    let totalUnits = 0;
    let oldestDate = null;
    for (const mkt of marketplaces) {
      if (!poolMktIds.has(mkt.id)) continue;
      const r = effectiveMarketplaceStock(mkt, today);
      if (!r) continue;
      contributors.push(r);
      totalUnits += r.units;
      if (!oldestDate || (r.date && r.date < oldestDate)) oldestDate = r.date;
    }
    if (totalUnits > 0) {
      // Dominant source = the one contributing the most units
      contributors.sort((a, b) => b.units - a.units);
      const dominantSource = contributors[0]?.source || "manual_product";
      return { units: totalUnits, date: oldestDate || todayString(today), source: dominantSource };
    }
  }
  // 3. Pool auto-baseline (no override flag)
  if (pool && Number(pool.initialStock) > 0) {
    return {
      units: Number(pool.initialStock),
      date: fallbackDate(pool.initialStockDate),
      source: "pool_baseline",
    };
  }
  // 4. Zero — legacy global fallback removed; inventory is tracked
  // exclusively through pools, marketplace stock, and supplier orders.
  return { units: 0, date: todayString(today), source: "none" };
}

// ─── Effective cost-per-unit for an inventory pool ───────────────────────────
// COG fallback hierarchy (highest priority first):
//   1. Pool's own initialCostPerUnit       — overrides everything; one number
//      drives the whole pool's Money in Inventory regardless of marketplace,
//      group, or product COGS.
//   2. Marketplace avgCogs                  — for marketplaces in this pool.
//      If multiple marketplaces are in the pool, weighted by pctOfTotal
//      (re-normalized within the pool). Marketplaces without an avgCogs set
//      fall through to their own group/product breakdown for that slice.
//   3. Group cogs                           — per-product fallback: if a
//      product is in a group with cogs set, use the group value.
//   4. Per-product cogs sum                 — productCost+shipping+customs+prep.
//   5. Zero                                 — user must enter manually.
//
// Returns a single number (the weighted average cost-per-unit for the pool).
// `marketplaces` is optional — without it the function only uses the pool's
// own value (matching pre-hierarchy behavior).
export function effectivePoolCostPerUnit(pool, marketplaces) {
  if (!pool) return 0;
  // 1. Pool override wins outright
  if (Number(pool.initialCostPerUnit) > 0) return Number(pool.initialCostPerUnit);
  if (!Array.isArray(marketplaces) || marketplaces.length === 0) return 0;
  // Find the marketplaces in this pool
  const poolMktIds = new Set(pool.marketplaceIds || []);
  const mktsInPool = marketplaces.filter(m => poolMktIds.has(m.id));
  if (mktsInPool.length === 0) return 0;
  // Weight each marketplace by its pctOfTotal, re-normalized to sum to 1
  // across the pool. Fall back to equal weights if pctOfTotal is missing.
  const totalPct = mktsInPool.reduce((s, m) => s + (Number(m.pctOfTotal) || 0), 0);
  const weightFor = (m) => totalPct > 0 ? (Number(m.pctOfTotal) || 0) / totalPct : 1 / mktsInPool.length;

  let weightedCost = 0;
  let weightAccum = 0;
  for (const mkt of mktsInPool) {
    const mktWeight = weightFor(mkt);
    if (mktWeight <= 0) continue;
    // 2. Marketplace-level avgCogs — applies to all products in this marketplace
    const mktAvg = Number(mkt.avgCogs) || 0;
    if (mktAvg > 0) {
      weightedCost += mktAvg * mktWeight;
      weightAccum += mktWeight;
      continue;
    }
    // 3 & 4. Per-product fallback within this marketplace, weighted by pctOfMarket.
    // For each product: group cogs (if its group has one) → product cogs sum → 0.
    const groups = mkt.productGroups || [];
    const groupOfProduct = (pid) => groups.find(g => Array.isArray(g.productIds) && g.productIds.includes(pid));
    let prodWeightedCost = 0;
    let prodWeightTotal = 0;
    for (const p of mkt.products || []) {
      if (p?._decoupled) continue;
      const w = Number(p?.pctOfMarket) || 0;
      if (w <= 0) continue;
      const g = groupOfProduct(p.id);
      const groupCogs = Number(g?.cogs) || 0;
      let perUnit = 0;
      if (groupCogs > 0) {
        perUnit = groupCogs;
      } else {
        const c = p.cogs || {};
        perUnit = (Number(c.productCost) || 0) + (Number(c.shipping) || 0) + (Number(c.customs) || 0) + (Number(c.prep) || 0);
      }
      if (perUnit > 0) {
        prodWeightedCost += perUnit * w;
        prodWeightTotal += w;
      }
    }
    if (prodWeightTotal > 0) {
      weightedCost += (prodWeightedCost / prodWeightTotal) * mktWeight;
      weightAccum += mktWeight;
    }
  }
  return weightAccum > 0 ? weightedCost / weightAccum : 0;
}

// ─── Auto-computed current stock from initial stock + orders - sales ─────────
export function computeCurrentStock({ initialStock, initialCostPerUnit, initialStockDate, orders, months, today, shrinkagePct = 0, pool = null, marketplaces = null }) {
  // Stage A — pick the baseline anchor via the new resolution hierarchy.
  // Pool override > per-marketplace cascade > pool baseline > legacy global > 0
  // The helper returns { units, date, source }; we use units+date as the
  // baseline that Stage B (orders + sales projection) walks forward from.
  let stockSource = "none";
  let effStock;
  let effDate;
  if (pool) {
    const anchor = effectivePoolStockAnchor(
      pool,
      marketplaces,
      { initialStock, initialStockDate },
      today,
    );
    effStock = anchor.units;
    effDate = anchor.date;
    stockSource = anchor.source;
  } else {
    // No pool — use the legacy global path directly (preserved for users
    // who never migrated to inventory pools).
    effStock = initialStock || 0;
    effDate = initialStockDate;
    stockSource = effStock > 0 ? "legacy_global" : "none";
  }
  // Cost-per-unit hierarchy: pool > marketplace > group > product. The helper
  // returns the highest-priority non-zero source available. When there's no
  // pool, fall back to the legacy initialCostPerUnit param (used by the old
  // global-inventory path before pools existed).
  let effCost;
  if (pool) {
    effCost = effectivePoolCostPerUnit(pool, marketplaces);
    if (effCost === 0 && (pool.initialCostPerUnit || 0) > 0) effCost = pool.initialCostPerUnit;
  } else {
    effCost = initialCostPerUnit || 0;
  }
  const effOrders = pool ? getOrdersForPool(orders, pool) : orders;
  initialStock = effStock; initialCostPerUnit = effCost; initialStockDate = effDate; orders = effOrders;
  const startDate = initialStockDate ? parseDate(initialStockDate) : null;
  if (initialStockDate && !startDate) return { currentStock: initialStock || 0, weightedCostPerUnit: initialCostPerUnit || 0, stockSource };

  let arrivedUnits = 0, arrivedValue = 0;
  for (const order of (orders || [])) {
    const orderUnits = order.unitCount || 0;
    const totalCt = (order.containerLines || []).reduce((s, l) => s + (l.qty || 0), 0) || (order.totalContainers || 0);
    // Multi-currency: use the stamped fxRate (same pattern as
    // computeMoneyBlockedInTransit below). Fallback to 1 only when the
    // rate is missing — never silently treat non-base-currency orders as
    // base-currency, which would understate inventory value by the full
    // FX delta (e.g. a CNY order shown at 1:1 vs EUR = ~7× off).
    const r = Number(order?.fxRate);
    const rate = (isFinite(r) && r > 0) ? r : 1;
    const orderValue = (Number(order.amount) || 0) * rate;
    for (const cb of (order.containerBatches || [])) {
      const arrival = parseDate(cb.arrivalDate);
      if (!arrival || arrival > today) continue;
      if (startDate && arrival < startDate) continue;
      let batchUnits = cb.unitCount !== undefined ? cb.unitCount : 0;
      if (!batchUnits && orderUnits > 0 && totalCt > 0) batchUnits = Math.round((cb.containers || 0) / totalCt * orderUnits);
      const batchValue = orderUnits > 0 ? batchUnits * (orderValue / orderUnits) : 0;
      arrivedUnits += batchUnits;
      arrivedValue += batchValue;
    }
  }

  let soldUnits = 0;
  const effStartDate = startDate || new Date(2020, 0, 1);

  for (const [key, mData] of Object.entries(months || {})) {
    if (!key.match(/^\d{4}-\d{2}$/)) continue;
    const [y, m] = key.split("-").map(Number);
    const dim = new Date(y, m, 0).getDate();
    const target = mData?.target || 0;
    if (target <= 0) continue;
    const dailyRate = target / dim;
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m - 1, dim);
    const effStart = monthStart < effStartDate ? effStartDate : monthStart;
    const effEnd = monthEnd > today ? today : monthEnd;
    if (effStart > effEnd) continue;
    const days = Math.max(0, Math.floor((effEnd.getTime() - effStart.getTime()) / 86400000) + 1);
    const isFullyPast = monthEnd < today;
    if (isFullyPast && mData?.actualSold > 0) {
      soldUnits += mData.actualSold;
    } else {
      soldUnits += Math.round(dailyRate * days);
    }
  }

  const effectiveArrived = Math.round(arrivedUnits * shrinkageMultiplier(shrinkagePct));
  const currentStock = Math.max(0, (initialStock || 0) + effectiveArrived - soldUnits);
  const initialValue = (initialStock || 0) * (initialCostPerUnit || 0);
  const totalValue = initialValue + arrivedValue;
  const totalUnits = (initialStock || 0) + effectiveArrived;
  const weightedCostPerUnit = totalUnits > 0 ? totalValue / totalUnits : (initialCostPerUnit || 0);

  return { currentStock, weightedCostPerUnit, arrivedUnits, soldUnits, totalValue: currentStock * weightedCostPerUnit, stockSource };
}

// ─── Stockout date projection ───────────────────────────────────────────────
export function computeStockoutDate({ currentStock, orders, months, today, shrinkagePct = 0, pool = null, targetScale = 1, resolveTarget = null }) {
  if (pool) orders = getOrdersForPool(orders, pool);
  if (currentStock <= 0) return { date: today, daysAway: 0 };
  let stock = currentStock;
  const todayY = today.getFullYear(), todayM = today.getMonth() + 1, todayD = today.getDate();
  const shrinkageMult = shrinkageMultiplier(shrinkagePct);

  const arrivals = {};
  for (const order of (orders || [])) {
    const orderUnits = order.unitCount || 0;
    const totalCt = (order.containerLines || []).reduce((s, l) => s + (l.qty || 0), 0) || (order.totalContainers || 0);
    for (const cb of (order.containerBatches || [])) {
      const arrival = parseDate(cb.arrivalDate);
      if (!arrival || arrival <= today) continue;
      let batchUnits = cb.unitCount !== undefined ? cb.unitCount : 0;
      if (!batchUnits && orderUnits > 0 && totalCt > 0) batchUnits = Math.round((cb.containers || 0) / totalCt * orderUnits);
      batchUnits = Math.round(batchUnits * shrinkageMult);
      const dk = `${arrival.getFullYear()}-${arrival.getMonth()+1}-${arrival.getDate()}`;
      arrivals[dk] = (arrivals[dk] || 0) + batchUnits;
    }
  }

  for (let i = 1; i <= 365; i++) {
    const d = new Date(todayY, todayM - 1, todayD + i);
    const y = d.getFullYear(), m = d.getMonth() + 1, day = d.getDate();
    const key = mkKey(y, m);
    const dim = new Date(y, m, 0).getDate();
    const target = resolveTarget ? resolveTarget(key) : (months?.[key]?.target || 0) * targetScale;
    const dailySales = target > 0 ? target / dim : 0;

    const dk = `${y}-${m}-${day}`;
    stock += (arrivals[dk] || 0);
    stock -= dailySales;

    if (stock <= 0) return { date: d, daysAway: i };
  }
  return null;
}

// ─── Stock time series — daily projection for Dashboard chart tooltip ──────
// Extends the point-in-time `computeCurrentStock` walk-forward into a
// per-day time series so the Dashboard tooltip can show "Blocked in
// Inventory: $X,XXX / Y,YYY units" at every chart point. Reuses the same
// resolution hierarchy (pool → marketplace → legacy) and the same
// arrival + sales walk-forward, but emits a value at each day.
//
// Returns a Map<"YYYY-M-D", { units, value }> (key format matches the
// `${year}-${month}-${day}` keys used by computeDailyData's internal
// date-keyed lookups).
export function computeStockTimeSeries({
  initialStock, initialCostPerUnit, initialStockDate,
  orders, months, today, shrinkagePct = 0,
  pool = null, marketplaces = null,
  startYear, endYear,
}) {
  // ── Resolve baseline (same as computeCurrentStock Stage A) ──────────
  let effStock, effCost, effOrders, effDate;
  if (pool) {
    const anchor = effectivePoolStockAnchor(
      pool, marketplaces,
      { initialStock, initialStockDate }, today,
    );
    effStock = anchor.units;
    effDate = anchor.date;
    effCost = effectivePoolCostPerUnit(pool, marketplaces);
    if (effCost === 0 && (pool.initialCostPerUnit || 0) > 0) effCost = pool.initialCostPerUnit;
    effOrders = getOrdersForPool(orders, pool);
  } else {
    effStock = initialStock || 0;
    effDate = initialStockDate;
    effCost = initialCostPerUnit || 0;
    effOrders = orders;
  }

  const anchorDate = effDate ? parseDate(effDate) : null;
  const costPerUnit = effCost || 0;
  const shrinkageMult = shrinkageMultiplier(shrinkagePct);

  // ── Pre-index arrivals by date key ──────────────────────────────────
  const arrivalsByDate = {};
  for (const order of (effOrders || [])) {
    const orderUnits = order.unitCount || 0;
    const totalCt = (order.containerLines || []).reduce((s, l) => s + (l.qty || 0), 0) || (order.totalContainers || 0);
    for (const cb of (order.containerBatches || [])) {
      const arrival = parseDate(cb.arrivalDate);
      if (!arrival) continue;
      if (anchorDate && arrival < anchorDate) continue;
      let batchUnits = cb.unitCount !== undefined ? cb.unitCount : 0;
      if (!batchUnits && orderUnits > 0 && totalCt > 0) batchUnits = Math.round((cb.containers || 0) / totalCt * orderUnits);
      batchUnits = Math.round(batchUnits * shrinkageMult);
      const dk = `${arrival.getFullYear()}-${arrival.getMonth() + 1}-${arrival.getDate()}`;
      arrivalsByDate[dk] = (arrivalsByDate[dk] || 0) + batchUnits;
    }
  }

  // ── Pre-index daily sales rate by month key ─────────────────────────
  const dailySalesByMonth = {};
  for (const [key, mData] of Object.entries(months || {})) {
    if (!key.match(/^\d{4}-\d{2}$/)) continue;
    const [y, m] = key.split("-").map(Number);
    const target = mData?.target || 0;
    if (target <= 0) continue;
    const dim = new Date(y, m, 0).getDate();
    dailySalesByMonth[key] = target / dim;
  }

  // ── Walk forward day by day ─────────────────────────────────────────
  const result = new Map();
  let stock = effStock;
  // Determine if we need to replay sold-before-anchor history. If anchor
  // is in the past, we need to walk from anchor to today subtracting
  // sales, then continue into the future. The simplest approach: walk
  // from Jan 1 of startYear through Dec 31 of endYear, only emitting
  // values and only subtracting sales after the anchor date.
  const walkStart = new Date(startYear, 0, 1);
  const walkEnd = new Date(endYear, 11, 31);
  const anchorMs = anchorDate ? anchorDate.getTime() : walkStart.getTime();

  for (let d = new Date(walkStart); d <= walkEnd; d.setDate(d.getDate() + 1)) {
    const y = d.getFullYear(), m = d.getMonth() + 1, day = d.getDate();
    const dMs = d.getTime();

    // Before anchor date — stock hasn't started yet
    if (dMs < anchorMs) {
      result.set(`${y}-${m}-${day}`, { units: 0, value: 0 });
      continue;
    }

    // Add arrivals
    const dk = `${y}-${m}-${day}`;
    stock += (arrivalsByDate[dk] || 0);

    // Subtract sales (same pro-rata logic as computeCurrentStock)
    const key = mkKey(y, m);
    const dailySales = dailySalesByMonth[key] || 0;
    // For past months with actualSold, use that instead (same logic as
    // computeCurrentStock lines 317-321). Approximate: distribute
    // actualSold evenly across the month's days.
    const mData = months?.[key];
    const dim = new Date(y, m, 0).getDate();
    const isFullyPast = new Date(y, m - 1, dim) < today;
    const effectiveDailySales = (isFullyPast && mData?.actualSold > 0)
      ? mData.actualSold / dim
      : dailySales;
    stock -= effectiveDailySales;

    // Clamp to zero
    stock = Math.max(0, stock);

    result.set(dk, { units: Math.round(stock), value: Math.round(stock * costPerUnit) });
  }

  return result;
}

// ─── Money blocked in transit ───────────────────────────────────────────────
// Counts cash that has been PAID for goods not yet arrived. Only items the
// user has explicitly marked as "paid" in Cash Calendar count — if a payment
// is overdue but not ticked, it's NOT counted (the user controls the
// narrative of when cash actually left the bank).
export function computeMoneyBlockedInTransit({ orders, months, today, pool = null }) {
  if (!Array.isArray(orders)) return 0;            // null-safety guard
  if (pool) orders = getOrdersForPool(orders, pool);
  let blocked = 0;
  for (const order of (orders || [])) {
    // Multi-currency: stamped at order creation, immutable. Same logic
    // as src/lib/calc/expenses.js orderFxRate(). Inlined here to avoid
    // a circular import between calc modules.
    const r = Number(order?.fxRate);
    const rate = (isFinite(r) && r > 0) ? r : 1;
    const baseAmt = (Number(order.amount) || 0) * rate;
    const totalCt = (order.containerLines || []).reduce((s, l) => s + (l.qty || 0), 0) || (order.totalContainers || 0);

    const pt0 = order.paymentTerms?.[0];
    if (pt0 && baseAmt > 0) {
      const pt0Id = `ord_${order.id}_pt0`;
      let pt0Paid = false;
      for (const [key, mData] of Object.entries(months || {})) {
        if ((mData?.orderExpPaid || {})[pt0Id]) { pt0Paid = true; break; }
      }
      if (pt0Paid) {
        const anyArrived = (order.containerBatches || []).some(cb => { const a = parseDate(cb.arrivalDate); return a && a <= today; });
        if (!anyArrived) blocked += baseAmt * (pt0.pct / 100);
      }
    }

    const shipCostPerCt = order.shippingCostPerContainer || 0;
    for (const cb of (order.containerBatches || [])) {
      const arrival = parseDate(cb.arrivalDate);
      const arrived = arrival && arrival <= today;
      if (arrived) continue;
      const shipId = `ord_${order.id}_ship_cb${cb.id}`;
      for (const [key, mData] of Object.entries(months || {})) {
        if ((mData?.orderExpPaid || {})[shipId]) {
          const shipAmt = (cb.shippingCostOverride != null ? cb.shippingCostOverride : shipCostPerCt) * (cb.containers || 0);
          blocked += shipAmt;
          break;
        }
      }
    }

    for (const cb of (order.containerBatches || [])) {
      const arrival = parseDate(cb.arrivalDate);
      const arrived = arrival && arrival <= today;
      if (arrived) continue;
      for (let ptIdx = 1; ptIdx < (order.paymentTerms || []).length; ptIdx++) {
        const ptId = `ord_${order.id}_pt${ptIdx}_cb${cb.id}`;
        for (const [key, mData] of Object.entries(months || {})) {
          if ((mData?.orderExpPaid || {})[ptId]) {
            let batchAmt;
            if (cb.customAmounts?.[ptIdx] != null) batchAmt = cb.customAmounts[ptIdx];
            else batchAmt = totalCt > 0 ? (baseAmt * ((order.paymentTerms[ptIdx]?.pct || 0) / 100) / totalCt) * (cb.containers || 0) : 0;
            blocked += batchAmt;
            break;
          }
        }
      }
    }

    if (order.incoterms === "ddu" && (order.dutyPct > 0 || order.dutyFlat > 0)) {
      for (const cb of (order.containerBatches || [])) {
        const arrival = parseDate(cb.arrivalDate);
        const arrived = arrival && arrival <= today;
        if (arrived) continue;
        const batchFraction = totalCt > 0 ? (cb.containers || 0) / totalCt : 1;
        const dutyAmt = order.dutyFlat > 0
          ? order.dutyFlat * batchFraction
          : baseAmt * ((order.dutyPct || 0) / 100) * batchFraction;
        if (dutyAmt > 0) blocked += dutyAmt;
      }
    }

    if (order.inspectionCost > 0) {
      const inspD = parseDate(order.inspectionDate);
      if (inspD && inspD <= today) {
        const anyArrived = (order.containerBatches || []).some(cb => { const a = parseDate(cb.arrivalDate); return a && a <= today; });
        if (!anyArrived) blocked += order.inspectionCost;
      }
    }
  }
  return blocked;
}

// ─── Compute inventory summary ─────────────────────────────────────────────
export function computeInventorySummary(marketplaces, months, today, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct = 0, pool = null) {
  // Forward marketplaces so computeCurrentStock can apply the COG hierarchy
  // (pool > marketplace.avgCogs > group.cogs > product.cogs > 0).
  const { currentStock, weightedCostPerUnit, stockSource } = computeCurrentStock({ initialStock, initialCostPerUnit, initialStockDate, orders, months, today, shrinkagePct, pool, marketplaces });
  const totalValue = currentStock * weightedCostPerUnit;
  const inTransitValue = computeMoneyBlockedInTransit({ orders, months, today, pool });

  let totalTargets = 0, totalDays = 0, monthsWithTargets = 0;
  for (const [key, mData] of Object.entries(months || {})) {
    if (!key.match(/^\d{4}-\d{2}$/)) continue;
    const t = mData?.target || 0;
    if (t > 0) {
      const [y, m] = key.split("-").map(Number);
      totalTargets += t;
      totalDays += getDaysInMonth(y, m);
      monthsWithTargets++;
    }
  }
  const dailySales = totalDays > 0 ? totalTargets / totalDays : 0;
  const _hasZeroMonths = monthsWithTargets > 0 && monthsWithTargets < Object.keys(months||{}).filter(k=>k.match(/^\d{4}-\d{2}$/)).length;
  const daysOfStock = dailySales > 0 ? Math.round(currentStock / dailySales) : null;

  let weightedDelay = 0, delayWeight = 0;
  for (const mkt of (marketplaces || [])) {
    const pct = Number(mkt.pctOfTotal) || 0;
    if (pct > 0) { weightedDelay += (Number(mkt.payoutDelay) || 0) * pct; delayWeight += pct; }
  }
  const avgPayoutDelay = delayWeight > 0 ? Math.round(weightedDelay / delayWeight) : 14;
  const avgCashRecoveryDays = daysOfStock != null ? daysOfStock + avgPayoutDelay : null;

  return { totalUnits: currentStock, totalValue, inTransitValue, avgCashRecoveryDays, weightedCostPerUnit, daysOfStock, avgPayoutDelay, hasZeroMonths: _hasZeroMonths, stockSource };
}
